import { useState, useEffect } from "react";

import { Container, Grid, Divider } from "@material-ui/core";
import { API_ENV } from "constants/environment";
import { useWebsocketFetch } from "hooks/useWebsocketFetch";
import { useVisionLogin } from "hooks/useVisionLogin";
import useForm from "hooks/form";

import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import LoadingData from "components/Loading/LoadingData";
import HeaderClientes from "components/_refactor/headers/clients";
import InputText from "components/_refactor/text/input";
import FilterCard from "components/_refactor/filter-card";
import StructureFields from "components/_refactor/fields/structure";
import AutoCompleteField from "components/_refactor/fields/autocomplete";
import PeriodField, {
  DEFAULT_PERIOD_OPTION,
  getMomentsForPresetDayCount,
  parseStoredPeriodDate,
} from "components/_refactor/fields/period-field";
import TableNotasFiscais from "./components/table-nfs";

const PERIOD_STORAGE_FORMAT = "MM-DD-YYYY";

function getDefaultPeriodDates() {
  const { mIni, mFim } = getMomentsForPresetDayCount(
    DEFAULT_PERIOD_OPTION.value,
  );
  return {
    dataInicial: mIni.format(PERIOD_STORAGE_FORMAT),
    dataFinal: mFim.format(PERIOD_STORAGE_FORMAT),
  };
}

const defaultPeriodDates = getDefaultPeriodDates();

const statusOptions = [
  {
    name: "Todas",
    Status: "",
  },
  {
    name: "NF Cancelada",
    Status: "NF CANCELADA",
  },
  {
    name: "NF Devolvida Parcialmente",
    Status: "NF DEVOLVIDA PARCIALMENTE",
  },
  {
    name: "NF Devolvida Totalmente",
    Status: "NF DEVOLVIDA TOTALMENTE",
  },
  {
    name: "NF Emitida",
    Status: "NF EMITIDA",
  },
];

export default function ConsultaNotasFiscais() {
  const snackbar = useSnackbar();
  const { userId } = useVisionLogin();

  const { data, handleFetchUrl, handleFetchData, status } = useWebsocketFetch({
    onSuccess: handleGetTableData,
  });

  const { values, handleChange, errors } = useForm({
    nrPedido: "",
    numeroNf: "",
    periodo: DEFAULT_PERIOD_OPTION,
    dataInicial: defaultPeriodDates.dataInicial,
    dataFinal: defaultPeriodDates.dataFinal,
  });

  const [searching, setSearching] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const [structureLoading, setStructureLoading] = useState([]);
  const [clearFilters, setClearFilters] = useState(false);
  const [structureFilters, setStructureFilters] = useState({});
  const [hasNoGroup, setHasNoGroup] = useState(false);
  const [storeListInvoices, setStoreListInvoices] = useState(null);
  const [invoiceStatus, setInvoiceStatus] = useState(statusOptions?.[0]);
  const [lastSearchParams, setLastSearchParams] = useState(null);

  const savingData = (tableData) => {
    if (tableData?.length !== 0) {
      setStoreListInvoices(tableData);
    } else {
      setStoreListInvoices([]);
    }
  };

  function handleGetTableData(tableData) {
    setSearching(false);
    savingData(tableData ?? data);
  }

  const resolveSearchDates = (periodo, dataInicial, dataFinal) => {
    if (periodo && periodo.value !== "Outros") {
      const { mIni, mFim } = getMomentsForPresetDayCount(periodo.value);
      if (!mIni?.isValid() || !mFim?.isValid()) return null;
      return {
        dtIni: mIni.format("YYYY-MM-DD"),
        dtFim: mFim.format("YYYY-MM-DD"),
      };
    }

    const mIni = parseStoredPeriodDate(dataInicial);
    const mFim = parseStoredPeriodDate(dataFinal);
    if (!mIni?.isValid() || !mFim?.isValid()) return null;

    return {
      dtIni: mIni.format("YYYY-MM-DD"),
      dtFim: mFim.format("YYYY-MM-DD"),
    };
  };

  const handleSearch = async () => {
    const { nrPedido, numeroNf, periodo, dataInicial, dataFinal } = values;

    const dates = resolveSearchDates(periodo, dataInicial, dataFinal);
    if (!dates) {
      snackbar.showMessage("Informe um período válido para a pesquisa.", {
        severity: "warning",
      });
      return;
    }

    setStoreListInvoices(null);
    setLastSearchParams(null);
    setSearching(true);
    setCancelLoading(false);
    setErrorMessage(null);

    setLastSearchParams({
      idMarca: structureFilters?.idMarca,
      idCanal: structureFilters?.idCanal,
      idRepresentante: structureFilters?.idRepresentante,
      idGrupoEconomico: structureFilters?.idGrupoEconomico,
      idLoja: structureFilters?.idLoja,
      dtIni: dates.dtIni,
      dtFim: dates.dtFim,
      numeroPedido: nrPedido,
      numeroNf: numeroNf,
      status: invoiceStatus?.Status || "",
    });
  };

  const handleSendMessage = (message) => {
    handleFetchData(message);
  };

  const handleClearFilters = () => {
    const resetDates = getDefaultPeriodDates();

    setClearFilters(true);
    handleChange({
      target: {
        name: "periodo",
        value: DEFAULT_PERIOD_OPTION,
      },
    });
    handleChange({
      target: {
        name: "dataInicial",
        value: resetDates.dataInicial,
      },
    });
    handleChange({
      target: {
        name: "dataFinal",
        value: resetDates.dataFinal,
      },
    });
    handleChange({
      target: {
        name: "nrPedido",
        value: "",
      },
    });
    handleChange({
      target: {
        name: "numeroNf",
        value: "",
      },
    });
    setInvoiceStatus(statusOptions?.[0]);
    setSearching(false);

    setTimeout(() => {
      setClearFilters(false);
    }, 500);
  };

  const handleStopSearching = () => {
    setCancelLoading(true);
    setSearching(false);
    handleFetchUrl("");
    setStoreListInvoices(null);
  };

  const handleVerifyRequiredInputs = () => {
    if (
      structureFilters?.idGrupoEconomico ||
      values.nrPedido ||
      values.numeroNf
    ) {
      return handleSearch();
    } else {
      if (
        !structureFilters?.idGrupoEconomico &&
        !values.nrPedido &&
        !values.numeroNf
      ) {
        setHasNoGroup(true);
      }
    }
  };

  useEffect(() => {
    if (errorMessage && !cancelLoading) {
      snackbar.showMessage(errorMessage, { severity: "error" });
    }
  }, [cancelLoading, errorMessage]);

  useEffect(() => {
    if (
      structureFilters.idGrupoEconomico !== "" ||
      values.nrPedido ||
      values.numeroNf
    ) {
      setHasNoGroup(false);
    }
  }, [structureFilters, values]);

  useEffect(() => {
    if (
      status === "empty" &&
      !cancelLoading &&
      !searching &&
      storeListInvoices?.length === 0
    ) {
      snackbar.showMessage("Sua busca não retornou nenhum resultado.", {
        severity: "success",
      });
    } else if (
      status === "empty" &&
      !cancelLoading &&
      searching &&
      storeListInvoices === null
    ) {
      setSearching(false);
      snackbar.showMessage("Sua busca não retornou nenhum resultado.", {
        severity: "success",
      });
    }
  }, [status, cancelLoading, searching, storeListInvoices]);

  useEffect(() => {
    if (lastSearchParams && userId) {
      handleSendMessage({
        action: "list-invoices-store",
        params: {
          querystring: lastSearchParams,
          header: {
            "K-IdUser": userId,
          },
        },
      });
    }
  }, [lastSearchParams, userId]);

  useEffect(() => {
    if (
      cancelLoading &&
      storeListInvoices === null &&
      lastSearchParams === null
    ) {
      handleFetchUrl(
        `wss://3zvsu3yj2b.execute-api.us-east-1.amazonaws.com/${API_ENV}`
      );
    }
  }, [cancelLoading, storeListInvoices, lastSearchParams]);

  useEffect(() => {
    handleFetchUrl(
      `wss://3zvsu3yj2b.execute-api.us-east-1.amazonaws.com/${API_ENV}`
    );
  }, []);

  return (
    <>
      <HeaderClientes
        section="Consulta de Notas Fiscais"
        subsection="Notas Fiscais"
        tooltipMessage="Para garantir melhor performance e usabilidade, a extração deste relatório será realizada em segundo plano (background). Isso permite que você continue utilizando a ferramenta do Portal Karsten normalmente enquanto o relatório é processado. Assim que a geração for concluída, o arquivo ficará disponível para download na central de relatório de Notas Fiscais."
      />
      <Container style={{ marginTop: "-4.5rem" }} maxWidth={false}>
        <FilterCard
          handleSearch={handleVerifyRequiredInputs}
          disabled={
            searching ||
            Object.entries(errors).length > 0 ||
            structureLoading.length > 0
          }
          searching={searching}
          handleClearFilters={handleClearFilters}
        >
          <StructureFields
            defaultBrand={{
              IdMarca: "TODAS",
              Nome: "Todas",
            }}
            clearFilters={clearFilters}
            onStructureFieldsChange={setStructureFilters}
            disableAll={searching}
            onStructureFieldsLoad={setStructureLoading}
            requiredFields={["economicGroup"]}
            fieldsOrder={["economicGroup", "store"]}
            hasNoGroup={hasNoGroup}
            handleSearch={handleVerifyRequiredInputs}
          />

          <Grid item xs={12}>
            <Divider style={{ margin: "0.5rem 0" }} />
          </Grid>

          <Grid item xs={12} md={6} lg={3}>
            <InputText
              label="Número do Pedido"
              value={values.nrPedido}
              onChange={(e) => {
                handleChange({
                  target: {
                    name: "nrPedido",
                    value: e.target.value.slice(0, 50),
                  },
                });
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleVerifyRequiredInputs();
                }
              }}
              disabled={searching}
              error={!!errors.nrPedido}
              errorMessage={errors.nrPedido}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <InputText
              label="Número da NF"
              value={values.numeroNf}
              onChange={(e) => {
                handleChange({
                  target: {
                    name: "numeroNf",
                    value: e.target.value.slice(0, 50),
                  },
                });
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleVerifyRequiredInputs();
                }
              }}
              disabled={searching}
              error={!!errors.numeroNf}
              errorMessage={errors.numeroNf}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <AutoCompleteField
              options={statusOptions}
              label="Status"
              value={invoiceStatus}
              onChange={(value) => {
                setInvoiceStatus(value);
              }}
              disabled={searching}
              loading={searching}
              optionLabelKey="name"
              optionIdKey="Status"
            />
          </Grid>
          <Grid
            item
            xs={12}
            md={values.periodo?.value === "Outros" ? "12" : 6}
            lg={values.periodo?.value === "Outros" ? "12" : 3}
          >
            <PeriodField
              label="Período"
              value={values.periodo}
              onChange={(option) =>
                handleChange({
                  target: {
                    name: "periodo",
                    value: option,
                  },
                })
              }
              initialDate={values.dataInicial}
              finalDate={values.dataFinal}
              setInitialDate={(value) =>
                handleChange({
                  target: {
                    name: "dataInicial",
                    value,
                  },
                })
              }
              setFinalDate={(value) =>
                handleChange({
                  target: {
                    name: "dataFinal",
                    value,
                  },
                })
              }
              disabled={searching || structureLoading.length > 0}
            />
          </Grid>
        </FilterCard>

        {storeListInvoices && !cancelLoading && !searching ? (
          <TableNotasFiscais dataTable={storeListInvoices} />
        ) : (
          <LoadingData
            searching={searching}
            onClick={() => handleStopSearching()}
            label="Buscando notas fiscais..."
          />
        )}
      </Container>
    </>
  );
}
